/**
 * Runtime platform detection.
 *
 * The same React bundle ships to three contexts:
 *   - web:       served from CloudFront → portal.zignagenix.com (browser tab)
 *   - electron:  desktop wrapper, exposes window.zignagenixAPI IPC bridge
 *   - capacitor: iOS / Android shells, exposes window.Capacitor + native plugins
 *
 * Library code (auth, storage, lifecycle) branches on this so the same call
 * sites work everywhere. We only detect once at module load — context can't
 * change mid-session.
 *
 * Phase M2.1, May 2 2026.
 */

const PLATFORM_WEB = 'web';
const PLATFORM_ELECTRON = 'electron';
const PLATFORM_CAPACITOR = 'capacitor';

function detectPlatform() {
  if (typeof window === 'undefined') {
    // SSR / Node test runners — treat as web.
    return PLATFORM_WEB;
  }

  // Capacitor injects a global before any user code runs. Check it first
  // because the desktop Electron build does NOT define this.
  if (window.Capacitor && typeof window.Capacitor.getPlatform === 'function') {
    const cap = window.Capacitor.getPlatform();
    if (cap === 'ios' || cap === 'android') {
      return PLATFORM_CAPACITOR;
    }
    // Capacitor returns 'web' when running in a browser via livereload.
    // Fall through to the other checks.
  }

  // Electron exposes the IPC bridge via preload.js. Identifies the desktop app.
  if (window.zignagenixAPI && typeof window.zignagenixAPI === 'object') {
    return PLATFORM_ELECTRON;
  }

  return PLATFORM_WEB;
}

const platform = detectPlatform();

export const PLATFORM = platform;
export const IS_WEB = platform === PLATFORM_WEB;
export const IS_ELECTRON = platform === PLATFORM_ELECTRON;
export const IS_CAPACITOR = platform === PLATFORM_CAPACITOR;
export const IS_MOBILE = platform === PLATFORM_CAPACITOR;

/**
 * Returns the underlying Capacitor platform string ('ios' | 'android')
 * or null on web/Electron. Useful for branching native-side behavior
 * (Apple Pay vs Google Pay, FLAG_SECURE concerns, etc.).
 */
export function capacitorPlatform() {
  if (!IS_CAPACITOR) return null;
  try {
    return window.Capacitor.getPlatform();
  } catch (e) {
    return null;
  }
}

export const PLATFORMS = Object.freeze({
  WEB: PLATFORM_WEB,
  ELECTRON: PLATFORM_ELECTRON,
  CAPACITOR: PLATFORM_CAPACITOR,
});
